import { useNeedPay } from 'modules/admin';
import { useEffect, useMemo, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as apiProductSet from 'api/apiProductSets';
import { generateRandomArticle } from 'components/forms/FormArticleInput';
import { useToast } from 'context/ToastContext';
import ESpinner from 'components/others/Spinner';
import type { SetEditFormValues } from '../types';
import SetEditForm from './SetEditForm';

interface OffSetEditProps {
    setId: number | null;
    setSetId: (setId: number | null) => void;
    fetchSets: (force?: boolean) => Promise<void>;
}

const emptySet: SetEditFormValues = {
    setArticle: '',
    setName: '',
    setDescription: '',
    categoryId: 0,
    dimensionId: 0,
    items: [],
};

const OffSetEdit = ({ setId, setSetId, fetchSets }: OffSetEditProps) => {
    const { t } = useTranslation(['Sets', 'common']);
    const { showToast } = useToast();
    const { needPay } = useNeedPay();
    const [setData, setSetData] = useState<SetEditFormValues>(emptySet);
    const [loading, setLoading] = useState(false);
    const [loadingSet, setLoadingSet] = useState(false);
    const isCreate = setId === 0;

    const title = useMemo(
        () => (isCreate ? t('edit.titleCreate') : t('edit.titleUpdate')),
        [isCreate, t],
    );

    const handleClose = () => {
        setSetId(null);
        setSetData(emptySet);
    };

    const fetchSet = async () => {
        if (!setId || isCreate) return;

        setLoadingSet(true);
        const resSet = await apiProductSet.getProductSet(setId);
        setLoadingSet(false);

        if (!resSet) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        setSetData({
            setArticle: resSet.setArticle || '',
            setName: resSet.setName || '',
            setDescription: resSet.setDescription || '',
            categoryId: Number(resSet.categoryId || 0),
            dimensionId: Number(resSet.dimensionId || 0),
            items:
                resSet.items?.map((item) => ({
                    productId: item.productId,
                    itemCount: item.itemCount,
                    prodName: item.prodName,
                    prodArticle: item.prodArticle,
                    vendorName: item.vendorName,
                    dimensionName: item.dimensionName,
                    defPrice: Number(item.prodDefPrice || 0),
                    defPriceSale: Number(item.prodDefPriceSale || 0),
                })) || [],
        });
    };

    const handleSubmit = async (payload: SetEditFormValues) => {
        if (isCreate && needPay()) return;
        setLoading(true);

        const apiPayload = {
            setArticle: payload.setArticle,
            setName: payload.setName,
            setDescription: payload.setDescription,
            categoryId: Number(payload.categoryId || 0),
            dimensionId: Number(payload.dimensionId || 0) || null,
            items: payload.items.map((item) => ({
                productId: item.productId,
                itemCount: item.itemCount,
            })),
        };

        if (isCreate) {
            const resCreate = await apiProductSet.createProductSet(apiPayload);
            setLoading(false);

            if (resCreate && typeof resCreate === 'object' && resCreate.articleDublicate) {
                showToast(t('messArticleExists'), 'danger');
                return;
            }

            if (!resCreate) {
                showToast(t('common:messError'), 'danger');
                return;
            }

            showToast(t('messSetCreated'), 'success');
            await fetchSets(true);
            handleClose();
            return;
        }

        const stat = await apiProductSet.updateProductSet(setId as number, apiPayload);
        setLoading(false);

        if (!stat) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        showToast(t('messSetUpdated'), 'success');
        await fetchSets(true);
        handleClose();
    };

    useEffect(() => {
        if (setId === null) return;
        if (isCreate) {
            setSetData({
                ...emptySet,
                setArticle: generateRandomArticle(),
            });
            return;
        }
        fetchSet();
    }, [setId]);

    return (
        <Offcanvas
            show={setId !== null}
            onHide={handleClose}
            placement="end"
            className="off-set-edit"
        >
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{title}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                {loadingSet ? (
                    <ESpinner />
                ) : (
                    <SetEditForm
                        key={String(setId)}
                        initialValues={setData}
                        loading={loading}
                        isCreate={isCreate}
                        submitLabel={isCreate ? t('toolAddSet') : t('common:buttons.save')}
                        onSubmit={handleSubmit}
                    />
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffSetEdit;
